import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { ButtonGroupModule } from 'primeng/buttongroup';
import { InputTextModule } from 'primeng/inputtext';
import { AutoFocusModule } from 'primeng/autofocus';
import { Product } from '@app/core/interfaces';
import { ToastMessageOptions } from 'primeng/api';
import { ProductService, UtilityService } from '@app/core/services';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectFilterEvent, SelectModule } from 'primeng/select';
import { debounceTime, Subject, takeUntil } from 'rxjs';

export interface ProductLineEmitter {
  property: string
  isSendingData: boolean
  message?: ToastMessageOptions
}

@Component({
  selector: 'app-product-detail-infos-input',
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    ButtonGroupModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    AutoFocusModule,
  ],
  templateUrl: './product-detail-infos-input.html'
})
export class ProductDetailInfosInput {
  @Input() product!: Product;
  @Input() label!: string;
  @Input() property!: string;
  @Input() type!: 'text' | 'number' | 'select' | 'link';
  @Input() suffix: string = '';
  @Input() filter: { [x: string]: string | number } = {};
  @Input('disabled') isDisabled: boolean = false;

  @Output() productChange = new EventEmitter<Product>(); // For two way binding of [(product)]
  @Output() isSendingData = new EventEmitter<ProductLineEmitter>();

  isEditMode: boolean = false;
  isLoadingMode: boolean = false;

  isSendingInputData: boolean = false;

  value: any = null;
  defaultValue: any = this.value;

  selectOptions: { id: number, nom: string }[] = []
  selectSubject = new Subject<string>();

  constructor(
    private productService: ProductService,
    private utilityService: UtilityService,
  ) {
    this.selectSubject.pipe(debounceTime(300)).subscribe(value => this.getSelectOptions(value));
  }

  ngOnInit(): void {
    this.value = this.property in this.product ? this.product[this.property] : '';
    this.defaultValue = this.property in this.product ? this.product[this.property] : '';
  }

  onToggleMode() {
    this.isEditMode = !this.isEditMode;

    if (this.isEditMode)
      this.getSelectOptions()
  }

  getSelectOptions(value?: string) {
    this.isLoadingMode = true;
    let requestFilter = { ...this.filter }
    if(value) requestFilter = {...requestFilter, nom: value}

    this.utilityService.getSelectOptionsObservable(this.property, requestFilter)
      .subscribe((data) => {
        this.selectOptions = data.results
        if (this.defaultValue && !this.selectOptions.find((option) => option.id === this.defaultValue.id))
          this.selectOptions.unshift(this.defaultValue)
        this.isLoadingMode = false
      })
  }

  onSelectFilter(event: SelectFilterEvent) {
    const value = event.filter;
    this.selectSubject.next(value);
  }

  onInputClose() {
    this.value = this.defaultValue;
    this.onToggleMode();
  }

  onSaveValue() {
    this.onToggleMode();

    if (this.defaultValue !== this.value) {
      this.isSendingInputData = true;
      this.isSendingData.emit({ property: this.property, isSendingData: true });
  
      const data: any = {};
      data[this.property] = this.value;
      data[`${this.property}_id`] = this.value?.id ?? this.value; // If select, send the id only
  
      this.productService
        .patch(this.product.id, data)
        .subscribe({
          next: (p: Product) => {
            this.product = {...this.product, ...data};
            this.defaultValue = this.value;
          },
          complete: () => {
            this.isSendingInputData = false;
            this.productChange.emit(this.product);
            this.isSendingData.emit({ property: this.property, isSendingData: false, message: { severity: 'info', summary: 'Modification', detail: `${this.label} a été changé avec succès` } });
          }
        })
    }
  }
}
